/**
 * TruyolSelector
 *
 * Panel que aparece dentro del PresupuestoGenerator cuando el usuario activa
 * "Buscar precio en Truyol". Permite seleccionar cualquier producto de la lista
 * maestra (catalogo_truyol_maestro) y, si está scrapeado, cargar los precios.
 *
 * Estados por producto (cruzando maestro × catalogo_truyol):
 *   ✅ verde — en catalogo_truyol con codes.producto_cod → precios live
 *   ⚠️ ámbar — en catalogo_truyol sin codes → solo precios cacheados
 *   ❌ gris — solo en maestro, no scrapeado → link a truyol.com
 *
 * Props:
 *   onPrecios(precios, nombre, plazo) — callback con array de {cantidad, coste_proveedor}
 *   onClose() — cierra el panel
 *   catalogoProductos — docs de catalogo_truyol (productos scrapeados con precios)
 *   catalogoMaestro   — docs de catalogo_truyol_maestro (lista completa de Truyol)
 */
import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, X, Loader2, AlertTriangle, CheckCircle2, MinusCircle } from "lucide-react";

const COTIZAR_URL = "https://truyol.test.roig.net/cotizar";
const PLAZO_PREFERIDO_LIVE = "4-5D";
const PLAZO_FALLBACK_PREF = ["4 a 5 días", "2 a 3 días", "6 a 7 días", "24 horas", "Recogida"];
const CANTIDADES_QUERY = [100, 250, 500, 1000];
const DEBOUNCE_MS = 300;

// Etiquetas legibles para columnas de plazo
const PLAZO_LABEL = {
  "24H": "24 h",
  "2-3D": "2-3 días",
  "4-5D": "4-5 días",
  "6-7D": "6-7 días",
};

// ¿Hay match entre un slug del maestro y alguno del catálogo scrapeado?
// Considera match si los slugs comparten un prefijo o sufijo separado por "-".
// Ej.: "tarjetas-de-visita" ↔ "tarjetas-de-visita-clasicas"
// Ej.: "alta-definicion"    ↔ "acabados-alta-definicion"
function maestroTieneScrapeado(maestroSlug, scrapeadoList) {
  return scrapeadoList.some(s => {
    const a = maestroSlug;
    const b = s.slug;
    if (a === b) return true;
    if (b.startsWith(a + "-")) return true; // maestro es prefijo del scrapeado
    if (a.startsWith(b + "-")) return true; // scrapeado es prefijo del maestro
    if (b.endsWith("-" + a)) return true;   // maestro es sufijo del scrapeado
    if (a.endsWith("-" + b)) return true;   // scrapeado es sufijo del maestro
    return false;
  });
}

export default function TruyolSelector({ onPrecios, onClose, catalogoProductos = [], catalogoMaestro = [] }) {
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState("");
  const [productoSeleccionado, setProductoSeleccionado] = useState(null); // doc del scrapeado (catalogo_truyol)
  const [productoMaestro, setProductoMaestro] = useState(null);            // doc del maestro (fallback gris)
  const [opciones, setOpciones] = useState({}); // { e_xxx: value }
  const [preciosLive, setPreciosLive] = useState(null); // { plazos, cantidades } | null
  const [plazoSeleccionado, setPlazoSeleccionado] = useState(PLAZO_PREFERIDO_LIVE);
  const [cargando, setCargando] = useState(false);
  const [usandoFallback, setUsandoFallback] = useState(false);
  const debounceRef = useRef(null);
  const fetchSeqRef = useRef(0);

  // Lista unificada del dropdown:
  //   1) Todos los scrapeados (catalogo_truyol) → ✅ live o ⚠️ cached
  //   2) Maestros que NO tienen ningún scrapeado emparentado → ❌ missing
  // Cada item lleva _estado y _source para el render y el handler.
  const baseList = useMemo(() => {
    const out = [];

    // Scrapeados con estado según codes
    for (const s of catalogoProductos) {
      out.push({
        ...s,
        _estado: s?.codes?.producto_cod ? "live" : "cached",
        _source: "scraped",
      });
    }

    // Maestros sin match → missing
    const slugsScrapeados = catalogoProductos.map(p => ({ slug: p.slug }));
    for (const m of catalogoMaestro) {
      if (maestroTieneScrapeado(m.slug, slugsScrapeados)) continue;
      out.push({
        ...m,
        _estado: "missing",
        _source: "maestro",
      });
    }

    return out;
  }, [catalogoProductos, catalogoMaestro]);

  const categorias = useMemo(() => {
    const set = [...new Set(baseList.map(p => p.categoria))];
    return set.sort();
  }, [baseList]);

  const productosDeCat = useMemo(() => {
    if (!categoriaSeleccionada) return [];
    return baseList
      .filter(p => p.categoria === categoriaSeleccionada)
      .sort((a, b) => {
        // Orden: live → cached → missing, luego alfabético
        const rank = { live: 0, cached: 1, missing: 2 };
        const d = rank[a._estado] - rank[b._estado];
        if (d !== 0) return d;
        return (a.nombre || a.slug).localeCompare(b.nombre || b.slug);
      });
  }, [baseList, categoriaSeleccionada]);

  // Lista ordenada de selects del producto, preservando orden de inserción
  const selectsLista = useMemo(() => {
    if (!productoSeleccionado?.selects) return [];
    return Object.entries(productoSeleccionado.selects).map(([key, val]) => ({
      key,
      label: val.label || key,
      options: val.options || [],
    }));
  }, [productoSeleccionado]);

  // Pedir cotización al endpoint
  const fetchCotizacion = useCallback(async (producto, opcionesActuales) => {
    if (!producto?.codes?.producto_cod) {
      setUsandoFallback(true);
      setPreciosLive(null);
      return;
    }
    const seq = ++fetchSeqRef.current;
    setCargando(true);
    try {
      const res = await fetch(COTIZAR_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slug: producto.slug,
          categoria: producto.categoria,
          codes: producto.codes,
          opciones: opcionesActuales,
          cantidades: CANTIDADES_QUERY,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (seq !== fetchSeqRef.current) return; // respuesta obsoleta
      if (!data?.cantidades?.length) throw new Error("Sin cantidades");
      setPreciosLive(data);
      setUsandoFallback(false);
      // Si el plazo preferido no está disponible, usar el primero
      if (!data.plazos?.includes(plazoSeleccionado)) {
        setPlazoSeleccionado(data.plazos?.[0] || PLAZO_PREFERIDO_LIVE);
      }
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      console.warn("[TruyolSelector] cotización falló, usando fallback:", err);
      setPreciosLive(null);
      setUsandoFallback(true);
    } finally {
      if (seq === fetchSeqRef.current) setCargando(false);
    }
  }, [plazoSeleccionado]);

  // Inicializar opciones cuando cambia el producto y disparar primera cotización
  useEffect(() => {
    if (!productoSeleccionado) {
      setOpciones({});
      setPreciosLive(null);
      setUsandoFallback(false);
      return;
    }
    const inicial = {};
    for (const [key, val] of Object.entries(productoSeleccionado.selects || {})) {
      const sel = (val.options || []).find(o => o.selected);
      inicial[key] = sel?.value ?? val.options?.[0]?.value ?? "";
    }
    setOpciones(inicial);
    setPlazoSeleccionado(PLAZO_PREFERIDO_LIVE);
    setPreciosLive(null);
    setUsandoFallback(false);
    fetchCotizacion(productoSeleccionado, inicial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productoSeleccionado]);

  // Debounce: cuando cambian las opciones, refetch
  const handleOpcionChange = (key, value) => {
    const nuevas = { ...opciones, [key]: value };
    setOpciones(nuevas);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchCotizacion(productoSeleccionado, nuevas);
    }, DEBOUNCE_MS);
  };

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const handleSeleccionarProducto = (item) => {
    if (!item) {
      setProductoSeleccionado(null);
      setProductoMaestro(null);
      return;
    }
    // Quitamos los flags internos antes de pasar el doc a la lógica de precios
    const { _estado, _source, ...doc } = item;
    if (_source === "scraped") {
      setProductoSeleccionado(doc);
      setProductoMaestro(null);
    } else {
      // Maestro sin match en scrapeado → solo CTA a truyol.com
      setProductoSeleccionado(null);
      setProductoMaestro(doc);
    }
  };

  const handleCargarPrecios = () => {
    if (!productoSeleccionado) return;

    // Camino 1: precios live disponibles
    if (preciosLive?.cantidades?.length) {
      const filas = preciosLive.cantidades
        .map(c => {
          const imp = c.importes?.[plazoSeleccionado];
          if (!imp) return null;
          return { cantidad: c.cnt, coste_proveedor: parseFloat(imp.sin_iva.toFixed(2)) };
        })
        .filter(Boolean)
        .sort((a, b) => a.cantidad - b.cantidad)
        .slice(0, 5);
      if (filas.length === 0) {
        alert("No hay precios para el plazo seleccionado.");
        return;
      }
      onPrecios(filas, productoSeleccionado.nombre, plazoSeleccionado);
      return;
    }

    // Camino 2: fallback a precios cacheados de Firestore
    const precios = productoSeleccionado.precios || [];
    if (precios.length === 0) {
      alert("Este producto no tiene precios scrapeados. Actualiza el scraper.");
      return;
    }
    let plazo = PLAZO_FALLBACK_PREF.find(p => precios.some(r => r.plazo === p));
    if (!plazo) plazo = precios[0]?.plazo;
    const porCantidad = {};
    for (const p of precios) {
      if (p.plazo === plazo) {
        porCantidad[p.cantidad] = p.precio_sin_iva || p.precio_con_iva / 1.21;
      }
    }
    const cantidades = Object.keys(porCantidad).map(Number).sort((a, b) => a - b).slice(0, 5);
    const resultado = cantidades.map(cant => ({
      cantidad: cant,
      coste_proveedor: parseFloat(porCantidad[cant].toFixed(2)),
    }));
    onPrecios(resultado, productoSeleccionado.nombre, plazo);
  };

  return (
    <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-4">
      <div className="flex justify-between items-center">
        <h5 className="font-semibold text-blue-800 dark:text-blue-200 text-sm flex items-center gap-2">
          🖨️ Buscar precio en Truyol
        </h5>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0">
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-blue-700 dark:text-blue-300">Categoría</Label>
          <select
            value={categoriaSeleccionada}
            onChange={e => {
              setCategoriaSeleccionada(e.target.value);
              setProductoSeleccionado(null);
              setProductoMaestro(null);
            }}
            className="w-full h-9 px-2 rounded-md border border-blue-200 dark:border-blue-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm"
          >
            <option value="">Seleccionar...</option>
            {categorias.map(cat => (
              <option key={cat} value={cat}>
                {cat.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
              </option>
            ))}
          </select>
        </div>

        <div>
          <Label className="text-xs text-blue-700 dark:text-blue-300">Producto</Label>
          <select
            value={(productoSeleccionado?.slug || productoMaestro?.slug) || ""}
            onChange={e => {
              const prod = productosDeCat.find(p => p.slug === e.target.value);
              handleSeleccionarProducto(prod || null);
            }}
            disabled={!categoriaSeleccionada}
            className="w-full h-9 px-2 rounded-md border border-blue-200 dark:border-blue-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm disabled:opacity-50"
          >
            <option value="">Seleccionar...</option>
            {productosDeCat.map(prod => {
              const icon = prod._estado === "live" ? "✅" : prod._estado === "cached" ? "⚠️" : "❌";
              return (
                <option key={prod.slug} value={prod.slug}>
                  {icon} {prod.nombre}
                </option>
              );
            })}
          </select>
        </div>
      </div>

      {productoMaestro && !productoSeleccionado && (
        <div className="space-y-2 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-3">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600">
              <MinusCircle className="w-3 h-3 mr-1" />
              Precios no disponibles
            </Badge>
            <span className="text-xs text-gray-600 dark:text-gray-400">{productoMaestro.nombre}</span>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Este producto no está scrapeado todavía. Consulta el precio directamente en truyol.com.
          </p>
          <a
            href={productoMaestro.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            Ver en Truyol <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}

      {productoSeleccionado && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            {usandoFallback ? (
              <Badge variant="outline" className="text-xs bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-950 dark:text-amber-200 dark:border-amber-800">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Precio orientativo — verificar en truyol.com
              </Badge>
            ) : preciosLive ? (
              <Badge variant="secondary" className="text-xs bg-green-100 text-green-800 border-green-200 dark:bg-green-950 dark:text-green-200">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Precio en vivo
              </Badge>
            ) : null}
            {productoSeleccionado.url && (
              <a
                href={productoSeleccionado.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1 hover:underline"
              >
                Ver en Truyol <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>

          {/* Opciones dinámicas del producto */}
          {selectsLista.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {selectsLista.map(sel => (
                <div key={sel.key}>
                  <Label className="text-xs text-blue-700 dark:text-blue-300">{sel.label}</Label>
                  <select
                    value={opciones[sel.key] ?? ""}
                    onChange={e => handleOpcionChange(sel.key, e.target.value)}
                    className="w-full h-9 px-2 rounded-md border border-blue-200 dark:border-blue-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm"
                  >
                    {sel.options.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}

          {/* Tabla de precios */}
          <div className="relative">
            {cargando && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-gray-900/60 rounded">
                <Loader2 className="w-5 h-5 animate-spin text-blue-600 dark:text-blue-400" />
              </div>
            )}
            {preciosLive ? (
              <PreciosLiveTabla
                data={preciosLive}
                plazoSeleccionado={plazoSeleccionado}
                onPlazoChange={setPlazoSeleccionado}
              />
            ) : (
              <PreciosResumen precios={productoSeleccionado.precios} />
            )}
          </div>

          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={handleCargarPrecios}
              disabled={cargando}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Cargar precios en sub-líneas →
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function PreciosLiveTabla({ data, plazoSeleccionado, onPlazoChange }) {
  // Ordenar plazos en el orden estable preferido
  const ORDEN = ["24H", "2-3D", "4-5D", "6-7D"];
  const plazos = ORDEN.filter(p => data.plazos?.includes(p));
  const cantidades = (data.cantidades || []).slice().sort((a, b) => a.cnt - b.cnt);

  if (cantidades.length === 0) {
    return <p className="text-xs text-gray-500">Sin precios disponibles.</p>;
  }

  return (
    <div className="rounded border border-blue-100 dark:border-blue-800 overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-blue-100 dark:bg-blue-900">
          <tr>
            <th className="text-left px-2 py-1 text-blue-700 dark:text-blue-300">Cant.</th>
            {plazos.map(p => {
              const activo = p === plazoSeleccionado;
              return (
                <th
                  key={p}
                  onClick={() => onPlazoChange(p)}
                  className={`text-right px-2 py-1 cursor-pointer select-none ${
                    activo
                      ? "bg-blue-200 dark:bg-blue-800 text-blue-900 dark:text-blue-100 font-semibold"
                      : "text-blue-700 dark:text-blue-300"
                  }`}
                  title="Clic para resaltar este plazo"
                >
                  {PLAZO_LABEL[p] || p}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {cantidades.map((c) => (
            <tr key={c.cnt} className="border-t border-blue-100 dark:border-blue-800 bg-white dark:bg-gray-900">
              <td className="px-2 py-1 font-medium">{c.cnt}</td>
              {plazos.map(p => {
                const imp = c.importes?.[p];
                const activo = p === plazoSeleccionado;
                return (
                  <td
                    key={p}
                    className={`px-2 py-1 text-right ${
                      activo
                        ? "bg-blue-50 dark:bg-blue-900/30 font-semibold text-blue-900 dark:text-blue-100"
                        : "text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {imp ? `${imp.sin_iva.toFixed(2)} €` : "—"}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[10px] text-gray-500 dark:text-gray-400 px-2 py-1 bg-gray-50 dark:bg-gray-900/50">
        Precios sin IVA. Plazo seleccionado: <strong>{PLAZO_LABEL[plazoSeleccionado] || plazoSeleccionado}</strong> (clic en otra columna para cambiar).
      </p>
    </div>
  );
}

function PreciosResumen({ precios = [] }) {
  const plazo = PLAZO_FALLBACK_PREF.find(p => precios.some(r => r.plazo === p)) || precios[0]?.plazo;

  const filas = precios
    .filter(p => p.plazo === plazo)
    .sort((a, b) => a.cantidad - b.cantidad)
    .slice(0, 5);

  if (filas.length === 0) return <p className="text-xs text-gray-500">Sin precios disponibles.</p>;

  return (
    <div className="rounded border border-blue-100 dark:border-blue-800 overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-blue-100 dark:bg-blue-900">
          <tr>
            <th className="text-left px-2 py-1 text-blue-700 dark:text-blue-300">Cant.</th>
            <th className="text-right px-2 py-1 text-blue-700 dark:text-blue-300">Sin IVA</th>
            <th className="text-right px-2 py-1 text-blue-700 dark:text-blue-300">Con IVA</th>
            <th className="px-2 py-1 text-blue-600 dark:text-blue-400 font-normal">Plazo</th>
          </tr>
        </thead>
        <tbody>
          {filas.map((p, i) => (
            <tr key={i} className="border-t border-blue-100 dark:border-blue-800 bg-white dark:bg-gray-900">
              <td className="px-2 py-1 font-medium">{p.cantidad}</td>
              <td className="px-2 py-1 text-right">{p.precio_sin_iva != null ? `${p.precio_sin_iva.toFixed(2)} €` : "—"}</td>
              <td className="px-2 py-1 text-right text-gray-500">{p.precio_con_iva != null ? `${p.precio_con_iva.toFixed(2)} €` : "—"}</td>
              <td className="px-2 py-1 text-gray-400">{p.plazo}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
